import axios, {
  AxiosRequestTransformer,
  AxiosResponseTransformer,
  ParamsSerializerOptions
} from 'axios'
import snakecaseKeys from 'snakecase-keys'
import camelcaseKeys from 'camelcase-keys'
import queryString from 'query-string'
import {
  getStoredAccessToken,
  getStoredRefreshToken,
  storeTokens,
  clearStoredTokens
} from '@services/token/tokenStorage'

const apiInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL as string,
  transformRequest: [
    function (data, headers) {
      // Don't transform data if it's FormData (for file uploads)
      if (data instanceof FormData) {
        // Important: We remove manually set Content-Type to let the browser set the correct boundary
        if (headers && headers['Content-Type'] === 'multipart/form-data') {
          delete headers['Content-Type']
        }
        return data
      }

      if (data) {
        // Convert proxy objects (like Formik/Jotai proxies) to plain objects
        const plainData = JSON.parse(JSON.stringify(data))
        return snakecaseKeys(plainData, { deep: true })
      }

      return data
    },
    ...(axios.defaults.transformRequest as AxiosRequestTransformer[])
  ],
  transformResponse: [
    ...(axios.defaults.transformResponse as AxiosResponseTransformer[]),
    function (data) {
      return data ? camelcaseKeys(data, { deep: true }) : data
    }
  ],
  paramsSerializer: {
    ...(axios.defaults.paramsSerializer as ParamsSerializerOptions),
    serialize: (param) => {
      if (param.lease_contract__investment_fk) {
        return queryString.stringify(param, { sort: false })
      }
      return queryString.stringify(snakecaseKeys(param, { deep: true }), { sort: false })
    }
  }
})

// Flag to prevent multiple refresh attempts
let isRefreshing = false
let refreshSubscribers: ((token: string) => void)[] = []

// Notify all subscribers when token is refreshed
const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token))
  refreshSubscribers = []
}

// Add request to queue to retry after token refresh
const addRefreshSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback)
}

// Refresh token function
const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = getStoredRefreshToken()
  if (!refreshToken) return null

  try {
    const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/admin/token/refresh/`, {
      refresh: refreshToken
    })

    const { access, refresh } = response.data
    // Backend rotates the refresh token, so persist BOTH the new access and the
    // new refresh token in every storage format (and notify atoms via event).
    storeTokens({ access, refresh: refresh ?? refreshToken })
    return access
  } catch (error) {
    console.error('Failed to refresh token:', error)
    return null
  }
}

// Add interceptors for request and response logging
apiInstance.interceptors.request.use(
  (config) => {
    // Add Bearer token if available
    const token = getStoredAccessToken()
    if (token) {
      config.headers.authorization = `Bearer ${token}`
    }

    // For FormData, make sure Content-Type is not set manually
    if (
      config.data instanceof FormData &&
      config.headers['Content-Type'] === 'multipart/form-data'
    ) {
      delete config.headers['Content-Type']
    }

    return config
  },
  (error) => {
    console.error('API Request Error:', error)
    return Promise.reject(error)
  }
)

apiInstance.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config

    console.error(
      'API Response Error:',
      error.response
        ? {
            status: error.response.status,
            url: error.config?.url,
            data: error.response.data
          }
        : error.message
    )

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip refresh for login and refresh endpoints
      if (
        originalRequest.url?.includes('/token/obtain/') ||
        originalRequest.url?.includes('/token/refresh/')
      ) {
        return Promise.reject(error)
      }

      originalRequest._retry = true

      if (!isRefreshing) {
        isRefreshing = true

        const newAccessToken = await refreshAccessToken()

        if (newAccessToken) {
          console.log('✅ Token refreshed via interceptor at:', new Date().toISOString())
          isRefreshing = false
          onRefreshed(newAccessToken)

          // Retry original request with new token
          originalRequest.headers.authorization = `Bearer ${newAccessToken}`
          return apiInstance(originalRequest)
        } else {
          console.log('❌ Token refresh failed, logging out at:', new Date().toISOString())
          // Refresh failed, clear tokens and redirect to login
          isRefreshing = false
          clearStoredTokens()

          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login'
          }

          return Promise.reject(error)
        }
      }

      // Wait for token refresh to complete
      return new Promise((resolve) => {
        addRefreshSubscriber((token: string) => {
          originalRequest.headers.authorization = `Bearer ${token}`
          resolve(apiInstance(originalRequest))
        })
      })
    }

    return Promise.reject(error)
  }
)

export default apiInstance
